/* eslint-disable react/jsx-one-expression-per-line */
import React, { useEffect } from 'react'
import { useHookstate } from '@hookstate/core'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Alert,
} from '@mui/material'
import FilterAutocompleteType from '~/components/FilterAutocompleteType'
import FilterAutocompleteSubtypesByType from '~/components/FilterAutocompleteSubtypeByType'
import IType from '~/features/Type/interfaces/IType'
// tipos mínimos usados apenas para comunicação com o componente pai
type MinimalType = { readonly id?: number | string } | null
type MinimalSubtype = { readonly id?: number } | null

interface Props {
  readonly open: boolean
  readonly onClose: () => void
  readonly typeIdValue: number | null
  readonly onTypeChange: (type: MinimalType) => void
  readonly subtypeIdValue: number | null
  readonly onSubtypeChange: (subtype: MinimalSubtype) => void
  readonly enabledField: boolean
  readonly verifyType: boolean
  readonly verifySubtype: boolean
  readonly invalidRequest: boolean
  readonly onSave: () => void
  readonly hasOccurrence?: boolean
  readonly occurrenceId?: number | null
}

export default function TypeSubtypeModal({
  open,
  onClose,
  typeIdValue,
  onTypeChange,
  subtypeIdValue,
  onSubtypeChange,

  enabledField,
  verifyType,
  verifySubtype,
  invalidRequest,
  onSave,
  hasOccurrence = false,
  occurrenceId = null,
}: Props) {
  const typeIdLocal = useHookstate<number | null>(null)
  const subtypeIdLocal = useHookstate<number | null>(null)

  useEffect(() => {
    if (open) {
      // Inicializa com os valores globais apenas ao abrir o modal
      typeIdLocal.set(typeIdValue)
      subtypeIdLocal.set(subtypeIdValue)
    }
    // Intencionalmente omitindo typeIdValue e subtypeIdValue para evitar loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const handleSave = () => {
    const tId = typeIdLocal.get()
    if (tId === null) {
      onTypeChange(null)
    } else {
      onTypeChange({ id: tId as number | string })
    }

    const sId = subtypeIdLocal.get()
    if (sId === null) {
      onSubtypeChange(null)
    } else {
      onSubtypeChange({ id: sId as number })
    }

    // Limpar os campos locais
    typeIdLocal.set(null)
    subtypeIdLocal.set(null)

    setTimeout(() => {
      onSave()
      onClose()
    }, 0)
  }

  const handleClose = () => {
    // Reverter valores locais para os valores globais ao cancelar
    typeIdLocal.set(typeIdValue)
    subtypeIdLocal.set(subtypeIdValue)
    onClose()
  }

  const handleTypeChange = (type: IType | null) => {
    const typeId = Number(type?.id) ?? null
    typeIdLocal.set(typeId)
    // Limpa o subtipo local quando o tipo muda
    subtypeIdLocal.set(null)
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogTitle>Selecionar Tipo e Subtipo</DialogTitle>
      <DialogContent>

        <Grid container spacing={2} pt={1}>
          <Grid item xs={12}>
            <FilterAutocompleteType
              typeId={typeIdLocal.get()}
              onTypeChange={handleTypeChange}
              error={verifyType && invalidRequest}
              disabled={hasOccurrence}
            />
          </Grid>
          <Grid item xs={12}>
            <FilterAutocompleteSubtypesByType
              disabled={typeIdLocal.get() === null || hasOccurrence}
              hasType
              typeId={typeIdLocal.get()}
              subtypeId={subtypeIdLocal.get()}
              onSubtypeChange={(subtype) => {
                subtypeIdLocal.set(Number(subtype?.id) ?? null)
              }}
              error={
                verifySubtype && typeIdLocal.get() !== null && invalidRequest
              }
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancelar</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={hasOccurrence}
        >
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  )
}
